import { useCachedFetch } from '@structural-climbing/auth'
import type { GymDisciplineMode, GymGradeConfig } from '@structural-climbing/validation'

export interface DaySchedule {
  open: string
  close: string
  closed: boolean
}

export interface WorkingHours {
  monday: DaySchedule
  tuesday: DaySchedule
  wednesday: DaySchedule
  thursday: DaySchedule
  friday: DaySchedule
  saturday: DaySchedule
  sunday: DaySchedule
}

export type DayKey = keyof WorkingHours

export interface GymBranding {
  id: string
  name: string
  description: string | null
  image_url: string | null
  cover_url: string | null
  /** Street address. Missing from a Worker older than this app. */
  address?: string | null
  primary_color: string | null
  secondary_color: string | null
  timezone: string | null
  working_hours: WorkingHours | null
  map_url: string | null
  map_file_type: 'pdf' | 'image' | null
  grade_config?: GymGradeConfig
  /** The gym is asking members who have no phone number to add one. */
  collect_missing_phones?: boolean
  /** Which disciplines the gym has. Missing on an older cached payload: both. */
  disciplines?: GymDisciplineMode
}

export function useGymBranding(gymId: string | null, apiUrl: string): GymBranding | null {
  // Keyed on gym id so the layout and the dashboard (both call this) share one
  // /gym-info request instead of firing it twice on the same screen.
  const { data } = useCachedFetch<GymBranding>(gymId ? `gym-info:${gymId}` : null, async () => {
    const res = await fetch(`${apiUrl}/gym-info/${gymId}`)
    if (!res.ok) throw new Error('Failed to load gym info')
    return (await res.json()) as GymBranding
  })

  return data ?? null
}
